// Phase 12 — Display Compliance Report (per-outlet / per-beat, bar chart of avg scores)
import { todayIST } from "@/lib/istDate";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, FileText as FileTextIcon } from "lucide-react";
import { api, formatApiError, API_BASE, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import PageHeader from "@/components/PageHeader";

export default function DisplayCompliancePage() {
  const [groupBy, setGroupBy] = useState("outlet");
  const [category, setCategory] = useState("");
  const [threshold, setThreshold] = useState(60);
  const [dateFrom, setDateFrom] = useState(() => todayIST());
  const [dateTo, setDateTo] = useState(() => todayIST());
  const [rows, setRows] = useState([]);
  const [cols, setCols] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = {
        group_by: groupBy, threshold,
        date_from: dateFrom, date_to: dateTo, page_size: 200,
      };
      if (category) params.category = category;
      const { data } = await api.get("/reports/display-compliance", { params });
      setRows(data.items || []);
      setCols(data.columns || []);
      setSummary(data.summary || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [groupBy, category, threshold, dateFrom, dateTo]);

  async function download(format) {
    const t = getToken();
    const params = new URLSearchParams();
    params.set("date_from", dateFrom); params.set("date_to", dateTo);
    params.set("group_by", groupBy); params.set("threshold", String(threshold));
    params.set("format", format);
    if (category) params.set("category", category);
    const url = `${API_BASE}/reports/display-compliance/export?${params.toString()}`;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${t}` } });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `display_compliance_${dateFrom}_to_${dateTo}.${format}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(String(e)); }
  }

  const chartRows = rows.slice(0, 15).map(r => ({
    name: groupBy === "beat" ? (r.beat_name || r.beat_code) : (r.retailer_name || r.retailer_code),
    avg: Number(r.avg_score) || 0,
  }));

  return (
    <div className="max-w-7xl mx-auto p-4" data-testid="display-compliance-page">
      <PageHeader
        breadcrumbs={[{ label: "Reports", to: "/" }, { label: "Display compliance" }]}
        title="Display compliance"
        subtitle="SKU display verification results by outlet with photo evidence."
        favorite={{ key: "reports.display-compliance", type: "screen", label: "Display compliance", route: "/reports/display-compliance" }}
        testid="displaycompliancepage-page-header"
      />

      <div className="mb-4">
        <h1 className="text-2xl font-semibold">Display Compliance</h1>
        <p className="text-sm text-neutral-500">
          Per {groupBy} average scoring for BTL / Display submissions.
          {summary && summary.avg_score != null && (
            <>  <span className="ml-2">Overall avg: <b>{summary.avg_score}</b> · compliant: <b>{summary.compliant_pct}%</b></span></>
          )}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
        <div>
          <Label>Group by</Label>
          <Select value={groupBy} onValueChange={setGroupBy}>
            <SelectTrigger data-testid="dc-group"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="outlet">Outlet</SelectItem>
              <SelectItem value="beat">Beat</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Category</Label>
          <Select value={category || "__both__"} onValueChange={(v) => setCategory(v === "__both__" ? "" : v)}>
            <SelectTrigger data-testid="dc-cat"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__both__">BTL + Display</SelectItem>
              <SelectItem value="btl">BTL only</SelectItem>
              <SelectItem value="display">Display only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Threshold</Label>
          <Input type="number" value={threshold} min={0} max={100}
                 onChange={(e) => setThreshold(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                 data-testid="dc-threshold" />
        </div>
        <div>
          <Label>From</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="dc-date-from" />
        </div>
        <div>
          <Label>To</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="dc-date-to" />
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <Button variant="outline" size="sm" onClick={() => download("xlsx")} data-testid="dc-export-xlsx">
          <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
        </Button>
        <Button variant="outline" size="sm" onClick={() => download("pdf")} data-testid="dc-export-pdf">
          <FileTextIcon className="w-4 h-4 mr-1" /> PDF
        </Button>
      </div>

      {chartRows.length > 0 && (
        <div className="bg-white border rounded-lg p-4 mb-4">
          <div className="text-sm text-neutral-500 mb-3">Top 15 by avg score</div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={chartRows} margin={{ top: 5, right: 20, left: 0, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" interval={0} height={70} />
                <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
                <Tooltip />
                <Bar dataKey="avg" fill="#059669" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-neutral-600 text-left">
            <tr>
              {cols.map(c => (
                <th key={c.key} className="px-3 py-2 font-medium whitespace-nowrap">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={cols.length} className="px-3 py-8 text-center text-neutral-500">Loading…</td></tr>}
            {!loading && rows.map((r, i) => (
              <tr key={i} className="border-t" data-testid={`dc-row-${i}`}>
                {cols.map(c => (
                  <td key={c.key} className="px-3 py-2 whitespace-nowrap">
                    {c.key === "compliant_pct" ? (
                      <span className={`font-semibold ${r[c.key] >= 60 ? "text-emerald-700" : "text-amber-700"}`}>{r[c.key]}%</span>
                    ) : c.key === "avg_score" ? (
                      <span className="font-semibold">{r[c.key]}</span>
                    ) : c.key === "last_submission_ts" && r[c.key] ? (
                      new Date(r[c.key]).toLocaleString()
                    ) : (r[c.key] ?? "—")}
                  </td>
                ))}
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={cols.length} className="px-3 py-8 text-center text-neutral-400">No compliance data yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
